import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../auth/AuthContext";
import KidsShell from "../components/KidsShell";
import IptvPlayer from "../components/IptvPlayer";

const PAGE_SIZE = 40;

export default function KidsTvPage() {
  const { token } = useAuth();
  const [params, setParams] = useSearchParams();
  const listRef = useRef(null);
  const loadingMoreRef = useRef(false);

  const [channels, setChannels] = useState([]);
  const [totalFiltered, setTotalFiltered] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");

  const activeId = params.get("ch") || "";

  const fetchChannels = useCallback(
    async ({ append = false, nextOffset = 0 } = {}) => {
      if (!token) return;
      if (append) {
        if (loadingMoreRef.current) return;
        loadingMoreRef.current = true;
        setLoadingMore(true);
      } else {
        setLoading(true);
        setError("");
      }
      try {
        const qs = new URLSearchParams({
          kind: "infantil",
          limit: String(PAGE_SIZE),
          offset: String(nextOffset),
        });
        const data = await api(`/api/iptv/channels?${qs.toString()}`, { token });
        const batch = data.channels || [];
        setTotalFiltered(data.total_filtered ?? batch.length);
        setHasMore(Boolean(data.has_more));
        setOffset(nextOffset + batch.length);
        setChannels((prev) => (append ? [...prev, ...batch] : batch));
      } catch (err) {
        if (!append) {
          setError(err.message || "No se pudo cargar la TV infantil");
          setChannels([]);
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
        loadingMoreRef.current = false;
      }
    },
    [token]
  );

  useEffect(() => {
    fetchChannels({ append: false, nextOffset: 0 });
  }, [fetchChannels]);

  const activeChannel = useMemo(
    () => channels.find((c) => c.id === activeId) || channels[0] || null,
    [channels, activeId]
  );

  useEffect(() => {
    if (!activeChannel || activeId) return;
    const p = new URLSearchParams(params);
    p.set("ch", activeChannel.id);
    setParams(p, { replace: true });
  }, [activeChannel, activeId, params, setParams]);

  function selectChannel(id) {
    const p = new URLSearchParams(params);
    p.set("ch", id);
    setParams(p, { replace: true });
  }

  function handleChannelFailed(failedId) {
    setChannels((prev) => prev.filter((c) => c.id !== failedId));
    setTotalFiltered((prev) => Math.max(0, prev - 1));
    if (activeId !== failedId) return;
    const p = new URLSearchParams(params);
    p.delete("ch");
    setParams(p, { replace: true });
  }

  function loadMore() {
    if (!hasMore || loadingMore) return;
    fetchChannels({ append: true, nextOffset: offset });
  }

  return (
    <KidsShell wide>
      <div className="kids-tv-page">
        <header className="kids-tv-head">
          <p className="kids-kicker">Casasm Kids</p>
          <h1>
            TV en vivo
          </h1>
          <p>Canales seguros · {totalFiltered} disponibles</p>
        </header>

        {error ? <p className="error">{error}</p> : null}

        <div className="kids-tv-layout">
          <aside className="kids-tv-list-wrap">
            <div className="kids-tv-list" ref={listRef}>
              {loading && !channels.length ? (
                <p className="kids-loading">Cargando canales…</p>
              ) : null}
              {channels.map((ch, i) => (
                <button
                  key={ch.id}
                  type="button"
                  className={`kids-tv-channel ${activeChannel?.id === ch.id ? "active" : ""}`}
                  style={{ "--kids-i": i }}
                  onClick={() => selectChannel(ch.id)}
                >
                  <span className="kids-tv-channel-icon" aria-hidden="true">
                    🧒
                  </span>
                  <span className="kids-tv-channel-text">
                    <strong>{ch.name}</strong>
                    {ch.group ? <small>{ch.group}</small> : null}
                  </span>
                </button>
              ))}
              {hasMore ? (
                <button
                  type="button"
                  className="kids-tv-load-more"
                  disabled={loadingMore}
                  onClick={loadMore}
                >
                  {loadingMore ? "Cargando…" : "Más canales"}
                </button>
              ) : null}
            </div>
          </aside>

          <section className="kids-tv-player-wrap">
            {activeChannel ? (
              <>
                <div className="kids-tv-player-head">
                  <h2>{activeChannel.name}</h2>
                  {activeChannel.group ? <p>{activeChannel.group}</p> : null}
                </div>
                <div className="kids-tv-player">
                  <IptvPlayer
                    channelId={activeChannel.id}
                    token={token}
                    channelName={activeChannel.name}
                    channelGroup={activeChannel.group}
                    onChannelFailed={handleChannelFailed}
                  />
                </div>
              </>
            ) : (
              <div className="kids-tv-empty">
                <p>No hay canales infantiles disponibles ahora.</p>
              </div>
            )}
          </section>
        </div>
      </div>
    </KidsShell>
  );
}
